/**
 * ProjectDashboard — per-project dashboard shown when a project is
 * selected in the tree. Shows project info + its elements as cards
 * + configurable widget area scoped to this project.
 */
import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Calendar, Zap, Archive, GitBranch, Flag, Users,
  BarChart2, BookOpen, Check, Target, Anchor, Navigation,
  ZapOff, FileText, Link2, Folder, MoreHorizontal,
  CheckCircle2, Clock, AlertTriangle, ChevronRight,
} from "lucide-react";

const TYPE_META = {
  calendar:    { label: "Calendar",      Icon: Calendar,   color: "#7C6CF8", route: null },
  workflow:    { label: "Workflow",       Icon: Zap,        color: "#F5C842", route: null },
  resources:   { label: "Resources",     Icon: Archive,    color: "#5EAA8A", route: null },
  automations: { label: "Automations",   Icon: Zap,        color: "#FF8A00", route: null },
  ab:          { label: "A/B",           Icon: GitBranch,  color: "#14B8A6", route: "/ab" },
  boards:      { label: "Progress",      Icon: Flag,       color: "#A855F7", route: "/boards" },
  courses:     { label: "Courses",       Icon: BookOpen,   color: "#60B8E0", route: "/courses" },
  commitments: { label: "Commitments",   Icon: Check,      color: "#00C781", route: "/commitments" },
  directory:   { label: "Directory",     Icon: Users,      color: "#EC4899", route: "/directory" },
  capture:     { label: "Capture",       Icon: Zap,        color: "#F59E0B", route: "/capture" },
  analytics:   { label: "Analytics",     Icon: BarChart2,  color: "#64B5F6", route: "/analytics" },
  compass:     { label: "Compass",       Icon: Target,     color: "#5A90F5", route: "/compass" },
  drift:       { label: "Drift",         Icon: Navigation, color: "#14B8A6", route: "/drift" },
  tension:     { label: "Tension",       Icon: ZapOff,     color: "#EF4444", route: "/tension" },
  buoy:        { label: "Buoy",          Icon: Anchor,     color: "#64B5F6", route: "/buoy" },
  handoff:     { label: "Handoff Briefs",Icon: FileText,   color: "#A855F7", route: "/handoff" },
  folder:      { label: "Folder",        Icon: Folder,     color: "#888892", route: null },
  shortcut:    { label: "Shortcut",      Icon: Link2,      color: "#888892", route: null },
};

// ─── Element card in the project grid ─────────────────────────────────────────
function ElementCard({ element, onNavigate }) {
  const meta = TYPE_META[element.type] || TYPE_META.shortcut;
  const Icon = meta.Icon;
  const color = element.color || meta.color;

  return (
    <button
      onClick={() => onNavigate(element)}
      className="flex flex-col gap-3 p-4 rounded-2xl text-left transition-all"
      style={{ background: "var(--cresc-surface-2)", border: "1px solid var(--cresc-border)" }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = color + "50"; e.currentTarget.style.background = "var(--cresc-surface-3)"; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--cresc-border)"; e.currentTarget.style.background = "var(--cresc-surface-2)"; }}>
      <div className="flex items-start justify-between">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: color + "20" }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <ChevronRight className="w-3.5 h-3.5 mt-1" style={{ color: "var(--cresc-text-4)" }} />
      </div>
      <div>
        <p className="text-[13px] font-semibold" style={{ color: "var(--cresc-text)" }}>{element.name}</p>
        <p className="text-[10px] mt-0.5" style={{ color: "var(--cresc-text-3)" }}>{meta.label}</p>
      </div>
    </button>
  );
}

// ─── Quick stats row ──────────────────────────────────────────────────────────
function StatPill({ label, value, color }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-xl" style={{ background: "var(--cresc-surface-2)" }}>
      <div className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
      <span className="text-[11px]" style={{ color: "var(--cresc-text-3)" }}>{label}</span>
      <span className="text-[13px] font-bold ml-auto" style={{ color: "var(--cresc-text)" }}>{value}</span>
    </div>
  );
}

// ─── Main ProjectDashboard ────────────────────────────────────────────────────
export default function ProjectDashboard({ project, onNavigateElement, onSetMainTab }) {
  const { current } = useWorkspace();
  const navigate = useNavigate();
  const wsId = current?.workspace_id;
  const [elements, setElements] = useState([]);
  const [commitments, setCommitments] = useState([]);
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!wsId || !project) return;
    setLoading(true);
    Promise.all([
      api.get(`/workspaces/${wsId}/elements`, { params: { project_id: project.project_id } }),
      api.get(`/workspaces/${wsId}/commitments`, { params: { status: "active" } }).catch(() => ({ data: [] })),
      api.get(`/workspaces/${wsId}/boards`).catch(() => ({ data: [] })),
    ]).then(([elRes, commRes, boardRes]) => {
      setElements((elRes.data || []).filter(e => !e.parent_id));
      setCommitments(commRes.data || []);
      setBoards(boardRes.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [wsId, project?.project_id]);

  const handleNavigate = useCallback((element) => {
    const meta = TYPE_META[element.type];
    // Environment types switch the main content area
    const envTypes = ["calendar", "resources", "automations"];
    if (envTypes.includes(element.type)) {
      onSetMainTab?.(element.type);
      return;
    }
    if (meta?.route) {
      navigate(meta.route);
      return;
    }
    onNavigateElement?.(element);
  }, [navigate, onSetMainTab, onNavigateElement]);

  const activeCommitments = commitments.filter(c => c.status === "active").length;
  const overdueCommitments = commitments.filter(c => c.status === "active" && c.due_date && c.due_date < new Date().toISOString()).length;
  const activeBoards = boards.filter(b => !b.archived).length;
  const elementCount = elements.length;

  // Group elements by type category
  const envElements = elements.filter(e => ["calendar","resources","automations","workflow"].includes(e.type));
  const toolElements = elements.filter(e => !["calendar","resources","automations","workflow","folder","shortcut"].includes(e.type));
  const orgElements = elements.filter(e => ["folder","shortcut"].includes(e.type));

  if (!project) return null;

  return (
    <div className="flex flex-col h-full overflow-y-auto" style={{ background: "var(--cresc-bg)", scrollbarWidth: "none" }}>
      {/* Project header */}
      <div className="px-8 pt-7 pb-5 flex-shrink-0">
        <div className="flex items-start gap-4">
          {/* Color dot — large */}
          <div className="w-12 h-12 rounded-2xl flex-shrink-0 mt-0.5" style={{ background: project.color + "30", border: `2px solid ${project.color}` }}>
            <div className="w-full h-full rounded-2xl flex items-center justify-center">
              <div className="w-4 h-4 rounded-full" style={{ background: project.color }} />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-[24px] font-bold tracking-tight" style={{ color: "var(--cresc-text)" }}>{project.name}</h1>
            {project.description && <p className="text-[13px] mt-1" style={{ color: "var(--cresc-text-3)" }}>{project.description}</p>}
          </div>
        </div>

        {/* Quick stats */}
        {!loading && (
          <div className="grid grid-cols-4 gap-2 mt-5">
            <StatPill label="Elements" value={elementCount} color={project.color} />
            <StatPill label="Active boards" value={activeBoards} color="#A855F7" />
            <StatPill label="Commitments" value={activeCommitments} color="#00C781" />
            {overdueCommitments > 0
              ? <StatPill label="Overdue" value={overdueCommitments} color="#EF4444" />
              : <StatPill label="On track" value="✓" color="#5EAA8A" />
            }
          </div>
        )}
      </div>

      <div className="mx-8 mb-5 h-px flex-shrink-0" style={{ background: "var(--cresc-border-sub)" }} />

      {/* Elements grid */}
      <div className="flex-1 px-8 pb-8">
        {loading ? (
          <div className="flex items-center justify-center py-16" style={{ color: "var(--cresc-text-4)" }}>
            <p className="text-[12px]">Loading…</p>
          </div>
        ) : elements.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: "var(--cresc-surface-2)", border: "1px solid var(--cresc-border)" }}>
              <div className="w-4 h-4 rounded-full" style={{ background: project.color + "60" }} />
            </div>
            <div className="text-center">
              <p className="text-[15px] font-semibold" style={{ color: "var(--cresc-text-3)" }}>No elements yet</p>
              <p className="text-[12px] mt-1" style={{ color: "var(--cresc-text-4)" }}>
                Hover the project name in the sidebar and click + to add elements
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Environments */}
            {envElements.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider mb-3" style={{ color: "var(--cresc-text-4)" }}>Environments</p>
                <div className="grid grid-cols-4 gap-3">
                  {envElements.map(el => <ElementCard key={el.element_id} element={el} onNavigate={handleNavigate} />)}
                </div>
              </div>
            )}

            {/* Tools */}
            {toolElements.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider mb-3" style={{ color: "var(--cresc-text-4)" }}>Tools</p>
                <div className="grid grid-cols-4 gap-3">
                  {toolElements.map(el => <ElementCard key={el.element_id} element={el} onNavigate={handleNavigate} />)}
                </div>
              </div>
            )}

            {/* Organize */}
            {orgElements.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider mb-3" style={{ color: "var(--cresc-text-4)" }}>Organize</p>
                <div className="grid grid-cols-4 gap-3">
                  {orgElements.map(el => <ElementCard key={el.element_id} element={el} onNavigate={handleNavigate} />)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
